import { SuggestionDisclosure } from './suggestion-disclosure.tsx';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { inboxActionsApi } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { offlineSessionIdentity } from '../state/offline-cache.ts';
import { Alert, Button, Select } from './ui.tsx';
import { useAiDisclosure } from './ai-disclosure.tsx';
import type { InboxActionSuggestion } from '../../shared/inbox-actions.ts';
import { InboxInstructionActionSuggestions } from './inbox-instruction-action-suggestions.tsx';
import { formatLocalTime, formatLongDate, formatNumericDate } from '../i18n/datetime.ts';
import { ApiRequestError } from '../api/client.ts';
import { usePendingSuggestions } from '../hooks/use-pending-suggestions.ts';
import { useSuggestionDisclosureMemory, type DisclosureMemory } from '../state/suggestion-disclosure-state.ts';

export function InboxActionSuggestions({ conversationId }: { conversationId: string }) {
  const session = useSession();
  const identity = JSON.stringify([
    offlineSessionIdentity(),
    session.user?.id,
    session.user?.role,
    session.company?.id,
    session.currentLocation?.id,
    session.user?.permissions,
    session.company?.accessLocked,
    session.company?.inboxActionsAvailable,
    session.company?.planFeatures,
    session.impersonatedBy,
  ]);
  const scope = useRef({ identity, conversationId, epoch: 0 });
  if (scope.current.identity !== identity || scope.current.conversationId !== conversationId)
    scope.current = { identity, conversationId, epoch: scope.current.epoch + 1 };
  const captured = scope.current;
  const scopedIdentity = `${identity}:${captured.epoch}`;
  const memory = useSuggestionDisclosureMemory(identity, `reschedule:${conversationId}`);
  if (
    !session.company?.inboxActionsAvailable ||
    session.user?.role === 'crew' ||
    !session.can('customers:read') ||
    !session.can('schedule:read') ||
    session.company.accessLocked
  )
    return null;
  return (
    <>
      <Suggestions
        key={`${conversationId}:${scopedIdentity}`}
        conversationId={conversationId}
        identity={scopedIdentity}
        memory={memory}
        scopeCurrent={() => scope.current === captured && offlineSessionIdentity() === JSON.parse(identity)[0]}
        canDecide={session.can('customers:write') && session.can('schedule:write')}
      />
      <InboxInstructionActionSuggestions conversationId={conversationId} />
    </>
  );
}

function Suggestions({
  conversationId,
  identity,
  canDecide,
  scopeCurrent,
  memory,
}: {
  conversationId: string;
  identity: string;
  canDecide: boolean;
  scopeCurrent: () => boolean;
  memory: DisclosureMemory;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const disclosure = useAiDisclosure();
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  // Returned proposals live in the context query. The mounted scope cannot
  // receive mutation text belonging to a location or user just left.
  const queryKey = ['inbox-actions', conversationId, identity];
  const context = useQuery({
    queryKey,
    queryFn: () => inboxActionsApi.context(conversationId),
    retry: false,
  });
  const preview = useRef({ data: context.data, denied: Boolean(context.error) });
  if (preview.current.data !== context.data || preview.current.denied !== Boolean(context.error))
    preview.current = { data: context.data, denied: Boolean(context.error) };
  const capturedPreview = preview.current;
  const contextCurrent = () => {
    const state = client.getQueryState(queryKey);
    return Boolean(preview.current === capturedPreview && context.data && state?.data === context.data && !state.error);
  };
  const [appointmentId, setAppointmentId] = useState('');
  // Preselect the visit the customer's message points at ("move Thursday's
  // clean"), once per loaded message, without overriding a manual choice.
  const preselected = useRef<string | null>(null);
  const autoSelected = useRef<string | null>(null);
  useEffect(() => {
    const source = context.data?.sourceMessageId ?? null;
    if (!source || preselected.current === source) return;
    preselected.current = source;
    const suggested = context.data?.suggestedVisitId;
    if (suggested && context.data?.visits.some((visit) => visit.id === suggested)) {
      autoSelected.current = suggested;
      setAppointmentId(suggested);
    } else if (autoSelected.current) {
      // A newer message that names no visit drops the previous message's
      // automatic pick; a visit chosen by hand stays.
      const previous = autoSelected.current;
      autoSelected.current = null;
      setAppointmentId((current) => (current === previous ? '' : current));
    }
  }, [context.data]);
  const session = useSession();
  const companyZone = session.currentLocation?.timezone ?? session.company?.timezone ?? null;
  const retriedAfterConsent = useRef(false);
  const selection = useRef({ appointmentId, sourceMessageId: context.data?.sourceMessageId });
  selection.current = { appointmentId, sourceMessageId: context.data?.sourceMessageId };
  const [error, setError] = useState<string | null>(null);
  const [empty, setEmpty] = useState(false);
  const request = useRef<{ key: string; id: string } | null>(null);
  const pendingRetry = useRef<{ id: string; appointmentId: string; sourceMessageId: string } | null>(null);
  const refresh = () => client.invalidateQueries({ queryKey: ['inbox-actions', conversationId, identity] });
  const suggest = useMutation({
    mutationFn: async (input: { id: string; appointmentId: string; sourceMessageId: string }) => {
      const current = () =>
        active.current &&
        scopeCurrent() &&
        contextCurrent() &&
        selection.current.appointmentId === input.appointmentId &&
        selection.current.sourceMessageId === input.sourceMessageId;
      await disclosure.ensure(true, current);
      if (!current()) throw new Error('stale-after-consent');
      return inboxActionsApi.suggest(conversationId, input);
    },
    onSuccess: async (value) => {
      if (active.current && scopeCurrent()) {
        request.current = null;
        retriedAfterConsent.current = false;
        setEmpty(!value.suggestion);
        await refresh();
      }
    },
    onError: async (caught, input) => {
      if (!active.current || !scopeCurrent()) return;
      console.error('Suggested visit move failed', caught);
      const reason = caught instanceof Error ? caught.message : '';
      if (reason === 'declined') {
        setError(t('inboxAction.declined'));
        return;
      }
      // Accepting the AI notice re-renders the panel, and the first request
      // straight after it could be dropped as out of date (or reach the
      // server before the acceptance did). Retry that one case once, with
      // fresh context, rather than showing a failure.
      const consentRace =
        reason === 'stale-after-consent' ||
        (caught instanceof ApiRequestError && caught.code === 'AI_DISCLOSURE_REQUIRED');
      if (consentRace && !retriedAfterConsent.current) {
        retriedAfterConsent.current = true;
        const fresh = await context.refetch();
        if (!active.current || !scopeCurrent() || fresh.error || !fresh.data) {
          setError(t('inboxAction.changed'));
          return;
        }
        if (
          fresh.data.sourceMessageId === input.sourceMessageId &&
          selection.current.appointmentId === input.appointmentId &&
          fresh.data.visits.some((visit) => visit.id === input.appointmentId)
        ) {
          // The refetch usually replaces the cached object, and this render's
          // closure still holds the old one; retry once the new one renders.
          if (fresh.data === context.data) suggest.mutate(input);
          else pendingRetry.current = input;
          return;
        }
        setError(t('inboxAction.changed'));
        return;
      }
      // A definite failure (busy provider, unreadable message) used up this
      // request id; the next click must send a fresh one. Only a handoff with
      // an unknown outcome keeps its id, so a retry finds the held request.
      // A transport failure may still have reached the server, so it keeps
      // the id as well.
      // A 409 (still preparing, held for credit review) or a 5xx may still be
      // running server side, so those keep the id too.
      if (caught instanceof ApiRequestError && caught.status === 400 && !/did not confirm/i.test(caught.message))
        request.current = null;
      if (consentRace || reason === 'stale') setError(t('inboxAction.changed'));
      else if (caught instanceof ApiRequestError && caught.status < 500 && caught.message)
        setError(t('inboxAction.errorReason', { reason: caught.message }));
      else setError(t('inboxAction.error'));
    },
  });
  useEffect(() => {
    const next = pendingRetry.current;
    if (!next || !context.data) return;
    pendingRetry.current = null;
    suggest.mutate(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.data]);
  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approve' | 'dismiss' }) => {
      if (!active.current || !scopeCurrent() || !contextCurrent()) throw new Error('stale');
      return inboxActionsApi.decide(id, decision);
    },
    onSuccess: async () => {
      if (active.current && scopeCurrent()) {
        setError(null);
        await refresh();
        await client.invalidateQueries({ queryKey: ['schedule'] });
      }
    },
    onError: () => {
      if (active.current && scopeCurrent()) {
        preview.current = { ...preview.current };
        setError(t('inboxAction.stale'));
      }
    },
  });
  const attentionKey = usePendingSuggestions(context.data?.suggestions);
  return (
    <>
      {disclosure.dialog}
      <SuggestionDisclosure
        title={t('inboxAction.title')}
        memory={memory}
        attentionKey={attentionKey}
        busy={context.isLoading || suggest.isPending || decide.isPending}
        failed={Boolean(context.error || error)}
      >
        {context.error ? (
          <Alert tone="error">
            {t('inboxAction.error')}
            <Button variant="secondary" onClick={() => void context.refetch()}>
              {t('common.retry')}
            </Button>
          </Alert>
        ) : !context.data ? (
          <p role="status">{t('common.loading')}</p>
        ) : (
          <>
            <p>{t('inboxAction.review')}</p>
            {canDecide && context.data.sourceMessageId && context.data.visits.length > 0 ? (
              <>
                <Select
                  label={t('inboxAction.visit')}
                  value={appointmentId}
                  onChange={(event) => setAppointmentId(event.target.value)}
                  options={[
                    { value: '', label: t('inboxAction.choose') },
                    ...context.data.visits.map((visit) => {
                      const parts = {
                        // The chooser can span years for quarterly or annual customers.
                        date: formatLongDate(visit.scheduled_date),
                        time: formatLocalTime(visit.start_time_local),
                        timezone: visit.timezone,
                      };
                      return {
                        value: visit.id,
                        label:
                          !companyZone || visit.timezone === companyZone
                            ? t('inboxAction.visitOption', parts)
                            : t('inboxAction.visitOptionZone', parts),
                      };
                    }),
                  ]}
                />
                <Button
                  variant="secondary"
                  busy={suggest.isPending}
                  disabled={!appointmentId || decide.isPending || suggest.isPending}
                  onClick={() => {
                    if (!active.current || !scopeCurrent() || !contextCurrent()) return;
                    setError(null);
                    setEmpty(false);
                    retriedAfterConsent.current = false;
                    const sourceMessageId = context.data.sourceMessageId!;
                    const key = JSON.stringify([appointmentId, sourceMessageId]);
                    if (request.current?.key !== key) request.current = { key, id: crypto.randomUUID() };
                    suggest.mutate({ id: request.current.id, appointmentId, sourceMessageId });
                  }}
                >
                  {t('inboxAction.suggest')}
                </Button>
              </>
            ) : null}
            {empty ? <p>{t('inboxAction.none')}</p> : null}
            {error ? (
              <Alert tone="error">
                {error}
                {decide.error ? (
                  <Button
                    variant="secondary"
                    disabled={context.isFetching}
                    onClick={async () => {
                      const result = await context.refetch();
                      if (active.current && scopeCurrent() && !result.error && result.data) {
                        decide.reset();
                        setError(null);
                      }
                    }}
                  >
                    {t('common.retry')}
                  </Button>
                ) : null}
              </Alert>
            ) : null}
            {context.data.suggestions.map((suggestion) => (
              <ActionCard
                key={suggestion.id}
                suggestion={suggestion}
                canDecide={canDecide && !decide.isPending && !suggest.isPending && !decide.error}
                onDecide={(decision) => {
                  if (active.current && scopeCurrent() && contextCurrent())
                    decide.mutate({ id: suggestion.id, decision });
                }}
              />
            ))}
          </>
        )}
      </SuggestionDisclosure>
    </>
  );
}

export function ActionCard({
  suggestion,
  canDecide,
  onDecide,
}: {
  suggestion: InboxActionSuggestion;
  canDecide: boolean;
  onDecide: (decision: 'approve' | 'dismiss') => void;
}) {
  const { t } = useTranslation();
  const [clock, setClock] = useState(Date.now);
  const expires = Date.parse(suggestion.expiresAt);
  useEffect(() => {
    const timer = setTimeout(() => setClock(Date.now()), Math.min(2_147_483_647, Math.max(0, expires - Date.now())));
    return () => clearTimeout(timer);
  }, [expires]);
  const pending = suggestion.status === 'pending' && expires > clock;
  return (
    <article className="mt-3 rounded border p-3">
      <h4 className="font-semibold">{suggestion.target.customerName}</h4>
      <p>
        {t('inboxAction.original', {
          date: formatNumericDate(suggestion.target.scheduledDate),
          time: formatLocalTime(suggestion.target.startTimeLocal),
          timezone: suggestion.target.timezone,
        })}
      </p>
      <p>{suggestion.proposal.summary}</p>
      <p>
        {t('inboxAction.move', {
          date: formatNumericDate(suggestion.proposal.scheduledDate),
          time: formatLocalTime(suggestion.proposal.startTimeLocal),
          timezone: suggestion.target.timezone,
        })}
      </p>
      <p>{t(`inboxAction.${suggestion.status}`)}</p>
      {pending && canDecide ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button onClick={() => onDecide('approve')}>{t('inboxAction.approve')}</Button>
          <Button variant="secondary" onClick={() => onDecide('dismiss')}>
            {t('inboxAction.dismiss')}
          </Button>
        </div>
      ) : null}
    </article>
  );
}
